import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDialog as MatDialogMdc } from '@angular/material/dialog';
import { TwoFactorGateDialogComponent } from '../../components/v2-layout/shared/two-factor-gate-dialog/two-factor-gate-dialog.component';
import { TwoFactorGateReason } from '../../enums/two-factor-gate-reason.enum';
import { Observable, from, EMPTY } from 'rxjs';
import { switchMap, catchError, filter, take, finalize } from 'rxjs/operators';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { TreasuryService } from './treasury.service';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { BankAccountInsightsService } from '../financial-accounts/bank-account-insights.service';
import { GrowlerService } from '../../components/shared/growler/growler.service';
import { BankAccountsService } from '../financial-accounts/bank-accounts.service';
import {
    BankAccountKycDialogComponent,
    BankAccountKycDialogResult,
} from '../../components/app-layout/shared/bank-account-kyc-dialog/bank-account-kyc-dialog.component';
import {
    BankAccountStripeOnboardingDialogComponent,
    BankAccountStripeOnboardingDialogInput,
} from '../../components/app-layout/shared/bank-account-stripe-onboarding-dialog/bank-account-stripe-onboarding-dialog.component';
import { MatDialogSizes } from '../../enums/mat-dialog-sizes.enum';
import { ONBOARDING_STATUSES } from '../../components/v2-layout/stripe-treasury/stripe-treasury.constants';
import { OrganizationBroadcastService, OrganizationEventType } from '../organization/organization-broadcast.service';

export interface FinancialConnectionsSessionResponse {
    clientSecret: string;
    sessionId: string;
    publishableKey: string;
    stripeAccount: string;
}

export interface ProcessAccountsRequest {
    accountIds: string[];
}

export interface ProcessedFcAccount {
    id: number;
    friendlyName: string;
    last4: string;
    routingNumber: string | null;
    sourceType: string;
    stripeFinancialConnectionsId: string;
    fcBalance: number | null;
}

export interface ProcessAccountsResponse {
    accounts: ProcessedFcAccount[];
}

@Injectable({
    providedIn: 'root',
})
export class FinancialConnectionsService {
    constructor(
        private _http: ApplicationHttpClient,
        private _treasuryService: TreasuryService,
        private _growler: GrowlerService,
        private _dialog: MatDialog,
        private _gateDialog: MatDialogMdc,
        private _bankAccountsService: BankAccountsService,
        private _orgBroadcast: OrganizationBroadcastService,
        private _bankAccountInsights: BankAccountInsightsService,
    ) {
        // SD-6865 broadcast adoption: replaces `_pollUntilTreasuryAccountReady`.
        // Backend dispatches `treasury.financial-account.ready` from
        // `TreasuryWebhookSubscriber::handleFinancialAccountFeaturesUpdated`
        // when outbound_transfers transitions to active (same trigger as the
        // payout-destination registration job). One fetch on receipt is all
        // that's needed — `createStream=true` pushes to `$bankAccounts`, so
        // subscribed deposit-account selectors flip to the readonly "PayHOA
        // Balance" state automatically.
        this._orgBroadcast.onEventType$(OrganizationEventType.TREASURY_FINANCIAL_ACCOUNT_READY)
            .pipe(
                // Defensive: Echo channels are already scoped to
                // `Organization.{orgId}`, but drop replayed or malformed
                // payloads that do not match the current JWT's orgId.
                filter((event) => LegFiJwtService.read()?.orgId === event.organizationId),
                switchMap((event) =>
                    this._bankAccountsService.getOrgBankAccounts(event.organizationId, null, true).pipe(take(1)),
                ),
            )
            .subscribe();
    }

    /**
     * Creates a Financial Connections session for linking external bank accounts.
     * Returns a clientSecret to initialize the Stripe Financial Connections modal.
     */
    public createSession(organizationId: number): Observable<FinancialConnectionsSessionResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsSessions(organizationId),
        );
        return this._http.post<FinancialConnectionsSessionResponse>(url, '{}');
    }

    /**
     * Processes linked accounts after the user completes the Stripe Financial Connections flow.
     * Sends the selected account IDs (fca_xxx format) to the backend for persistence.
     */
    public processAccounts(
        organizationId: number,
        accountIds: string[],
    ): Observable<ProcessAccountsResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccounts(organizationId),
        );
        const body: ProcessAccountsRequest = { accountIds };
        return this._http.post<ProcessAccountsResponse>(url, JSON.stringify(body));
    }

    /**
     * Get the Stripe-provided default friendly name for an FC account.
     */
    public getDefaultName(
        organizationId: number,
        fcAccountId: string,
    ): Observable<{ defaultName: string }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccountDefaultName(organizationId, fcAccountId),
        );
        return this._http.get<{ defaultName: string }>(url);
    }

    /**
     * Trigger a manual balance refresh and sync for an FC account.
     */
    public syncBalance(
        organizationId: number,
        fcAccountId: string,
    ): Observable<{ success: boolean }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsAccountSyncBalance(organizationId, fcAccountId),
        );
        return this._http.post<{ success: boolean }>(url, '{}');
    }

    /**
     * Open the Stripe Financial Connections modal and collect accounts.
     * Loads Stripe.js dynamically and presents the FC modal to the user.
     */
    public async collectAccounts(session: FinancialConnectionsSessionResponse): Promise<any> {
        const { loadStripe } = await import('@stripe/stripe-js');
        const stripe = await loadStripe(session.publishableKey, {
            stripeAccount: session.stripeAccount,
        });

        if (!stripe) {
            throw new Error('Failed to load Stripe.js');
        }

        return stripe.collectFinancialConnectionsAccounts({
            clientSecret: session.clientSecret,
        });
    }

    /**
     * Link an FC account and sync balance for an external bank account created during KYC.
     * The fcAccountId comes from a completed Financial Connections session.
     */
    public linkExternalAccountBalance(
        organizationId: number,
        ubaId: number,
        fcAccountId: string,
    ): Observable<{ success: boolean }> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.FinancialConnectionsLinkExternalBalance(organizationId, ubaId),
        );
        return this._http.post<{ success: boolean }>(url, JSON.stringify({ fcAccountId }));
    }

    /**
     * Add a bank account — picks the right flow based on the Treasury feature flag
     * and the org's Connect verification status.
     *
     * Flow selection:
     * - Feature flag OFF → onStandardDialog; caller owns the legacy dialog. onComplete does NOT fire.
     * - Feature flag ON + Connect status 'verified' → opens Stripe Financial Connections modal.
     * - Feature flag ON + Connect status !== 'verified' → opens the KYC dialog (v1), then the
     *   Stripe Connect onboarding dialog (v1) if the user elects to verify.
     *
     * Callback contract:
     * - onStandardDialog: fires only on the non-Treasury path; caller opens the legacy dialog.
     * - onFcSuccess: fires after Financial Connections accounts are successfully linked (verified path only).
     * - onComplete: fires once when the Treasury flow (FC or KYC) ends, regardless of outcome.
     *   Safe to reset loading state here. Does NOT fire on the onStandardDialog path.
     */
    public addBankAccount(
        organizationId: number,
        onStandardDialog: () => void,
        onFcSuccess: () => void,
        onComplete: () => void,
    ): void {
        if (!this._bankAccountInsights.hasTreasuryEnabled()) {
            onStandardDialog();
            return;
        }

        this._treasuryService.connectStatus$.pipe(
            filter((status) => status !== null),
            take(1),
            switchMap((status) => {
                if (status !== ONBOARDING_STATUSES.VERIFIED) {
                    this._openKycFlow(organizationId, onComplete);
                    return EMPTY;
                }

                return this.createSession(organizationId).pipe(
                    switchMap((session) => from(this.collectAccounts(session))),
                    switchMap((result) => {
                        if (result.error) {
                            return EMPTY;
                        }
                        const accounts = result.financialConnectionsSession?.accounts ?? [];
                        if (accounts.length === 0) {
                            return EMPTY;
                        }
                        const accountIds = accounts.map((account: any) => account.id);
                        return this.processAccounts(organizationId, accountIds);
                    }),
                    catchError((error) => {
                        this._growler.fromErrorResponse(error);
                        return EMPTY;
                    }),
                    finalize(() => onComplete()),
                );
            }),
        ).subscribe({
            next: (result) => {
                if (result) {
                    const count = result.accounts.length;
                    this._growler.success(
                        'Account linked',
                        count === 1
                            ? 'Bank account linked successfully.'
                            : `${count} bank accounts linked successfully.`,
                    );
                    onFcSuccess();
                }
            },
        });
    }

    /**
     * Open the KYC / onboarding flow for a Treasury-enabled org that has not yet
     * completed verification. Always shows the v1 KYC intro dialog first, then —
     * on "Start Verification" — opens the Stripe Connect onboarding dialog. Fires
     * onComplete when the flow ends.
     */
    private _openKycFlow(organizationId: number, onComplete: () => void): void {
        this._dialog.open(BankAccountKycDialogComponent, {
            panelClass: 'legacy-kyc-dialog',
            width: MatDialogSizes.MD,
            maxWidth: MatDialogSizes.MD,
            disableClose: true,
            autoFocus: true,
        }).afterClosed().subscribe({
            next: (result: BankAccountKycDialogResult) => {
                if (result?.startVerification) {
                    this._openStripeOnboardingDialog(organizationId, onComplete);
                } else {
                    onComplete();
                }
            },
        });
    }

    private _openStripeOnboardingDialog(organizationId: number, onComplete: () => void): void {
        // SD-8102: gate mandatory-2FA users before v1 Connect onboarding (parity with
        // the v2 KYC interception). TREASURY_KYC is a soft gate — dismissing abandons setup.
        // SD-8330: keyed on mfaEnforced, not mfaRequired — pre-verification mfaRequired is false; this
        // admin-only path is where the enroll-before-KYC prompt belongs.
        if (LegFiJwtService.read()?.mfaEnforced) {
            TwoFactorGateDialogComponent.open(this._gateDialog, TwoFactorGateReason.TREASURY_KYC)
                .afterClosed()
                .pipe(take(1))
                .subscribe((enrolled) => {
                    if (enrolled === true) {
                        this._launchStripeOnboardingDialog(organizationId, onComplete);
                    } else {
                        onComplete();
                    }
                });

            return;
        }

        this._launchStripeOnboardingDialog(organizationId, onComplete);
    }

    private _launchStripeOnboardingDialog(organizationId: number, onComplete: () => void): void {
        const data: BankAccountStripeOnboardingDialogInput = { organizationId };
        this._dialog.open(BankAccountStripeOnboardingDialogComponent, {
            width: BankAccountStripeOnboardingDialogComponent.width,
            maxWidth: BankAccountStripeOnboardingDialogComponent.width,
            disableClose: true,
            autoFocus: true,
            data,
        }).afterClosed().subscribe(() => {
            // Stripe's embedded onboarding has no success/cancel signal — `setOnExit`
            // fires either way. Post-KYC live updates are now broadcast-driven
            // (SD-6865 broadcast adoption):
            //   - `.treasury.connect-status.updated` flips `connectStatus$` to
            //     verified once `account.updated` lands with capability=active.
            //   - `.treasury.financial-account.ready` triggers the bank-accounts
            //     refresh once outbound_transfers features become active.
            // Both events are dispatched from `TreasuryWebhookSubscriber`, so no
            // local orchestration (refresh + poll) is needed here.
            onComplete();
        });
    }
}
